"use client";

import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Shield,
  ShieldCheck,
  Loader2,
  Copy,
  Download,
  AlertTriangle,
  Smartphone,
  KeyRound,
  Info,
} from "lucide-react";
import {
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@repo/ui";

import {
  setupStaff2fa,
  verifyStaff2faSetup,
  disableStaff2fa,
  regenerateStaffRecoveryCodes,
  get2faStatus,
} from "@/lib/api/auth";

type SetupStep = "idle" | "intro" | "qr" | "codes" | "enabled";

interface TwoFactorSectionProps {
  userInitials: string;
}

export function TwoFactorSection({ userInitials }: TwoFactorSectionProps) {
  const [step, setStep] = useState<SetupStep>("idle");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [qrUri, setQrUri] = useState("");
  const [secret, setSecret] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");
  const [showRegen, setShowRegen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    get2faStatus()
      .then((res) => {
        if (res?.staffEnabled) {
          setIsEnabled(true);
          setStep("enabled");
        }
      })
      .catch(() => {});
  }, []);

  const handleStartSetup = () => {
    setStep("intro");
    setDialogOpen(true);
    setError("");
    setVerifyCode("");
  };

  const handleProceedToQr = async () => {
    setIsLoading(true);
    setError("");
    try {
      const res = await setupStaff2fa();
      setQrUri(res.qrUri);
      setSecret(res.secret);
      setStep("qr");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to start setup";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const res = await verifyStaff2faSetup({
        code: verifyCode,
        type: "totp",
      });
      setRecoveryCodes(res.recoveryCodes);
      setIsEnabled(true);
      setStep("codes");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Invalid code";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      await disableStaff2fa({ password: disablePassword });
      setIsEnabled(false);
      setStep("idle");
      setShowDisable(false);
      setDisablePassword("");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Invalid password";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const res = await regenerateStaffRecoveryCodes({
        password: regenPassword,
      });
      setRecoveryCodes(res.recoveryCodes);
      setStep("codes");
      setDialogOpen(true);
      setShowRegen(false);
      setRegenPassword("");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Invalid password";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (step === "codes") {
      setStep("enabled");
    } else if (step === "intro" || step === "qr") {
      setStep("idle");
      setVerifyCode("");
    }
    setDialogOpen(false);
    setError("");
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const downloadRecoveryCodes = () => {
    const content = `C2 Vault Recovery Codes for ${userInitials}\n${"=".repeat(40)}\n\n${recoveryCodes.join("\n")}\n\nKeep these codes in a safe place.\nEach code can only be used once.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `c2vault-recovery-codes-${userInitials.toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-xl border bg-muted/30">
          <div className="flex items-center gap-3">
            {isEnabled ? (
              <ShieldCheck className="h-5 w-5 text-green-600" />
            ) : (
              <Shield className="h-5 w-5 text-muted-foreground" />
            )}
            <div className="space-y-1">
              <p className="text-sm font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-muted-foreground">
                {isEnabled
                  ? "Your account is protected with 2FA"
                  : "Add an extra layer of security to your account"}
              </p>
            </div>
          </div>
          {step === "idle" && (
            <Button
              variant="outline"
              onClick={handleStartSetup}
              disabled={isLoading}
              className="gap-2"
            >
              <Shield className="h-4 w-4" />
              Set Up
            </Button>
          )}
          {step === "enabled" && !showDisable && !showRegen && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRegen(true)}
              >
                Regenerate Codes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDisable(true)}
                className="text-destructive hover:text-destructive"
              >
                Disable
              </Button>
            </div>
          )}
        </div>

        {error && !dialogOpen && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Disable form */}
        {showDisable && (
          <form
            onSubmit={handleDisable}
            className="rounded-xl border p-4 space-y-4"
          >
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-800">
                  Are you sure you want to disable 2FA?
                </p>
                <p className="text-xs text-amber-700">
                  Your account will only be protected by your password. This is
                  not recommended for accounts with access to controlled
                  substance records.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="disable-2fa-password">
                Enter your password to confirm
              </Label>
              <Input
                id="disable-2fa-password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDisable(false);
                  setDisablePassword("");
                  setError("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={isLoading || !disablePassword}
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Disable 2FA
              </Button>
            </div>
          </form>
        )}

        {/* Regenerate form */}
        {showRegen && (
          <form
            onSubmit={handleRegenerate}
            className="rounded-xl border p-4 space-y-4"
          >
            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-800">
                  Regenerating recovery codes
                </p>
                <p className="text-xs text-blue-700">
                  Your current recovery codes will be invalidated and replaced
                  with new ones. Make sure to save the new codes.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="regen-2fa-password">
                Enter your password to confirm
              </Label>
              <Input
                id="regen-2fa-password"
                type="password"
                value={regenPassword}
                onChange={(e) => setRegenPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRegen(false);
                  setRegenPassword("");
                  setError("");
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || !regenPassword}>
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Regenerate
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Setup Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          // Prevent accidental dismissal during recovery codes step
          if (!open && step === "codes") return;
          handleDialogClose();
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => {
            if (step === "codes") e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (step === "codes") e.preventDefault();
          }}
        >
          {/* Step 1: Intro */}
          {step === "intro" && (
            <>
              <DialogHeader>
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <DialogTitle className="text-center">
                  Set Up Two-Factor Authentication
                </DialogTitle>
                <DialogDescription className="text-center">
                  Add an extra layer of security to your staff account
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Smartphone className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">
                        You'll need an authenticator app
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Google Authenticator, Authy, or Microsoft Authenticator.
                        Download one if you don't have it already.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <KeyRound className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">
                        You'll get recovery codes
                      </p>
                      <p className="text-xs text-muted-foreground">
                        10 one-time backup codes in case you lose access to your
                        authenticator app. Save them somewhere safe.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <ShieldCheck className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">What changes</p>
                      <p className="text-xs text-muted-foreground">
                        After setup, you'll enter a 6-digit code from your
                        authenticator app each time you log in with your
                        initials and password.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-col gap-2 sm:flex-col">
                <Button
                  onClick={handleProceedToQr}
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Continue
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleDialogClose}
                  className="w-full"
                >
                  Cancel
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Step 2: QR Code */}
          {step === "qr" && (
            <>
              <DialogHeader>
                <DialogTitle className="text-center">Scan QR Code</DialogTitle>
                <DialogDescription className="text-center">
                  Open your authenticator app and scan this code to link your
                  account
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 py-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-center">
                  <div className="p-4 bg-white rounded-xl border shadow-sm">
                    <QRCodeSVG value={qrUri} size={180} />
                  </div>
                </div>

                <details className="group">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Can't scan? Enter this code manually
                  </summary>
                  <code className="block mt-2 p-3 rounded-lg bg-muted text-xs font-mono break-all select-all">
                    {secret}
                  </code>
                </details>

                <form onSubmit={handleVerify} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="2fa-verify-code" className="text-sm">
                      Enter the 6-digit code from your app
                    </Label>
                    <Input
                      id="2fa-verify-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      placeholder="000000"
                      value={verifyCode}
                      onChange={(e) =>
                        setVerifyCode(e.target.value.replace(/\D/g, ""))
                      }
                      autoComplete="one-time-code"
                      autoFocus
                      className="text-center text-lg tracking-widest font-mono"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || verifyCode.length !== 6}
                    className="w-full"
                  >
                    {isLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Verify & Activate
                  </Button>
                </form>
              </div>
            </>
          )}

          {/* Step 3: Recovery Codes */}
          {step === "codes" && recoveryCodes.length > 0 && (
            <>
              <DialogHeader>
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <ShieldCheck className="h-6 w-6 text-green-600" />
                </div>
                <DialogTitle className="text-center">
                  2FA is now active
                </DialogTitle>
                <DialogDescription className="text-center">
                  Save your recovery codes before closing this window
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-800">
                      Save these codes now
                    </p>
                    <p className="text-xs text-amber-700">
                      Each code can only be used once. If you lose your
                      authenticator device, these are the only way to access
                      your account. You won't see them again.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 p-4 rounded-lg bg-muted">
                  {recoveryCodes.map((code, i) => (
                    <code
                      key={i}
                      className="text-sm font-mono text-center py-1"
                    >
                      {code}
                    </code>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyRecoveryCodes}
                    className="gap-2 flex-1"
                  >
                    <Copy className="h-4 w-4" />
                    {copied ? "Copied!" : "Copy All"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadRecoveryCodes}
                    className="gap-2 flex-1"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button onClick={handleDialogClose} className="w-full">
                  I've saved my codes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
